import PeriodisationNudge, { periodisationAnswerForConfirm } from '../../Accounting/Periodisering/PeriodisationNudge';
import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { AlertTriangle } from 'lucide-react';
import dayjs from 'dayjs';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { fetchPaymentAccounts } from '../../../../services/api/chartRolesApi';
import BrandSelect from '../../Common/BrandSelect';
import '../../Common/ll-modal.css';

const fmtKr = (v) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Number(v) || 0);

/**
 * RegisterPaymentModal — register a payment on a customer invoice, or the
 * refund of a credit note. One dialog for the detail page and the board;
 * both used to build their own SweetAlert out of an HTML string.
 *
 * The betalkonto options are the tenant's own payment accounts, loaded when
 * the modal opens — never a hardcoded list.
 *
 * Props:
 *   show, onClose
 *   companyId
 *   invoice: { id, number, customerName, invoiceDate } — null while closed
 *   remaining: amount still open; prefilled and the upper bound
 *   isRefund: credit-note refund wording
 *   onRegistered: ({ amount, date, account }) => void, after the API accepted it
 */
const RegisterPaymentModal = ({ show, onClose, companyId, invoice, remaining, isRefund = false, onRegistered }) => {
  const today = dayjs().format('YYYY-MM-DD');
  const open = Number(remaining) || 0;

  const [asPeriodisation,setAsPeriodisation]=useState(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today);
  const [account, setAccount] = useState('');
  const [accounts, setAccounts] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setAmount(open.toFixed(2));
    setDate(today);
    setError(null);
    setSaving(false);
    setAccounts(null);
    fetchPaymentAccounts(companyId)
      .then((list) => {
        if (cancelled) return;
        setAccounts(list);
        setAccount(list[0]?.accountNumber || '');
      })
      .catch(() => { if (!cancelled) setAccounts([]); });
    return () => { cancelled = true; };
    // Re-initialise only when the modal opens for an invoice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, invoice?.id]);

  const value = parseFloat(String(amount).replace(',', '.'));
  const invoiceDate = (invoice?.invoiceDate || '').slice(0, 10);
  const noAccounts = accounts !== null && accounts.length === 0;

  let amountError = null;
  if (amount !== '' && !(value > 0)) amountError = 'Beloppet måste vara större än 0.';
  else if (value > open + 0.01) amountError = `Beloppet överstiger kvarvarande ${fmtKr(open)}.`;

  let dateError = null;
  if (!date) dateError = 'Ange ett datum.';
  else if (date > today) dateError = 'Datumet kan inte ligga i framtiden.';
  else if (invoiceDate && date < invoiceDate) dateError = 'Datumet kan inte vara före fakturadatum.';

  const leftAfter = value > 0 && !amountError ? Math.max(open - value, 0) : null;
  const periodisationGate = periodisationAnswerForConfirm(asPeriodisation);
  const canSubmit = !saving && !amountError && !dateError && value > 0 && account && !noAccounts && !periodisationGate.blocked;

  const handleClose = () => { if (!saving) onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        getApiUrl(`${companyId}/invoices/${invoice.id}/register-payment`),
        { paidAmount: value, paymentDate: date, selectedAccount: account, asPeriodisation: periodisationGate.asPeriodisation },
        { withCredentials: true }
      );
      onRegistered?.({ amount: value, date, account });
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message
        || (isRefund ? 'Kunde inte registrera återbetalningen. Försök igen.' : 'Kunde inte registrera betalningen. Försök igen.')
      );
      setSaving(false);
    }
  };

  const title = isRefund ? 'Registrera återbetalning' : 'Registrera betalning';

  return (
    <Modal show={show} onHide={handleClose} centered className="ll-modal">
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{title}</Modal.Title>
            {invoice && (
              <p className="ll-modal__subtitle">
                {[invoice.number, invoice.customerName].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </Modal.Header>

        <Modal.Body>
          {show && !isRefund && value>0 && account && <PeriodisationNudge companyId={companyId} sourceType="customer_invoice" sourceId={invoice?.id} date={date}
            lines={[]} payment={{date,amount:value,accountId:account}} onChange={setAsPeriodisation}/>}
          <div className="ll-modal__summary">
            <span>{isRefund ? 'Att återbetala' : 'Kvar att betala'}</span>
            <strong>{fmtKr(open)}</strong>
          </div>

          {error && (
            <div className="ll-modal__error" role="alert">
              <AlertTriangle size={15} />
              <span>{error}</span>
            </div>
          )}

          {noAccounts && (
            <div className="ll-modal__error" role="alert">
              <AlertTriangle size={15} />
              <span>Inga betalkonton hittades i kontoplanen — kontrollera kontoplanen under Inställningar → Bokföring.</span>
            </div>
          )}

          <div className="ll-field__row">
            <div className="ll-field">
              <label htmlFor="pay-amount">Belopp</label>
              <div className="ll-input-unit">
                <input
                  id="pay-amount"
                  type="number"
                  inputMode="decimal"
                  className={`ll-input${amountError ? ' is-invalid' : ''}`}
                  value={amount}
                  min="0.01"
                  step="0.01"
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                />
                <span>kr</span>
              </div>
            </div>
            <div className="ll-field">
              <label htmlFor="pay-date">{isRefund ? 'Återbetalningsdatum' : 'Betalningsdatum'}</label>
              <input
                id="pay-date"
                type="date"
                className={`ll-input${dateError ? ' is-invalid' : ''}`}
                value={date}
                min={invoiceDate || undefined}
                max={today}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          {(amountError || dateError || leftAfter > 0.005) && (
            <div className="ll-field" style={{ marginTop: '-0.5rem' }}>
              {amountError && <span className="ll-field__hint is-error">{amountError}</span>}
              {dateError && <span className="ll-field__hint is-error">{dateError}</span>}
              {!amountError && leftAfter > 0.005 && (
                <span className="ll-field__hint">
                  Delbetalning — {fmtKr(leftAfter)} återstår efter detta.{' '}
                  <button type="button" onClick={() => setAmount(open.toFixed(2))}>Hela beloppet</button>
                </span>
              )}
            </div>
          )}

          <div className="ll-field">
            <label htmlFor="pay-account">{isRefund ? 'Återbetalning skickad från konto' : 'Betalning mottagen på konto'}</label>
            <BrandSelect
              inputId="pay-account"
              name="account"
              options={(accounts || []).map((a) => ({ value: a.accountNumber, label: `${a.accountNumber} — ${a.accountName}` }))}
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder={accounts === null ? 'Laddar konton…' : 'Välj konto…'}
              isDisabled={accounts === null || noAccounts}
              isSearchable={(accounts || []).length > 6}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <span className="ll-modal__note" />
          <div className="ll-modal__actions">
            <button type="button" className="ll-btn ll-btn--secondary" onClick={handleClose} disabled={saving}>
              Avbryt
            </button>
            <button type="submit" className="ll-btn ll-btn--primary" disabled={!canSubmit}>
              {saving && <Spinner animation="border" size="sm" />}
              {title}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default RegisterPaymentModal;
